import {
  SelectContent,
  SelectItem,
  SelectPrimitiveProps,
  Select as SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select';

import React from 'react';
import { Label } from '../';

import { cn } from '@/helpers/ui-utils';
import HelperText from '../helperText';

export type Option = {
  value: string;
  label: string;
};

export type Options = Option[];

export type OnValueChangeParams = {
  value: string;
  label: string;
  name?: string;
};

export type SelectProps = Omit<SelectPrimitiveProps, 'onValueChange'> &
  React.InputHTMLAttributes<HTMLSelectElement> & {
    /**
     * The name of the select input.
     * This is used to identify the input when submitting a form.
     *
     * @type {string}
     */
    name: string;

    /**
     * The label for the select input,
     * providing context to the user about what the dropdown represents.
     *
     * @type {string}
     */
    label?: string;

    /**
     * An array of options that will be displayed in the dropdown.
     * Each option must contain a `label` and a `value for correct rendering.
     * The `Options` type should be an array of objects in the following format:
     *
     * ```tsx
     * type Option = { label: string, value: string };
     * ```
     *
     * @type {Options}
     */
    options: Options;

    /**
     * A callback function that gets triggered when the selected value changes.
     * It provides the selected value and name of the select input
     *
     * Example of usage:
     *
     * ```ts
     *  const handleValueChange = ({ value, label }) => {
     *    console.info(`Selected value: ${value} from input: ${label}`);
     *  };
     * ```
     *
     * @param {OnValueChangeParams} option
     */
    onValueChange?: (option: OnValueChangeParams) => void;

    /**
     * A boolean flag that indicates if the select input is in an invalid state.
     * When set to `true`, the select will be visually highlighted as invalid.
     *
     * @type {boolean}
     * @default false
     */
    invalid?: boolean;

    /**
     * Helper text to display below the select input.
     *
     * @type {string}
     * @default undefined
     */
    helperText?: string;

    /**
     * Callback function that is triggered when the select input loses focus.
     *
     * @type {(event: React.FocusEvent<HTMLButtonElement>) => void}
     * @default undefined
     */
    onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  };

/**
 * Present users with a dropdown to choose from predefined options with keyboard navigation and accessibility support.
 *
 * @example
 * ```tsx
 * <Select
 *   label="Select a fruit"
 *   name="fruit"
 *   options={[
 *     { label: 'Apple', value: 'apple' },
 *     { label: 'Banana', value: 'banana' },
 *     { label: 'Orange', value: 'orange' },
 *   ]}
 *   onValueChange={({ value, label }) => console.info(`Selected value: ${value} from input: ${label}`)}
 * />
 * ```
 */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      name,
      label,
      defaultValue,
      onValueChange,
      invalid,
      placeholder,
      options,
      className,
      onBlur,
      helperText,
      ...props
    },
    ref,
  ) => {
    const rootProps = {
      ref,
      name: name,
      defaultValue: defaultValue ? `${defaultValue}` : undefined,
      ...(onValueChange && {
        onValueChange: (value: string) => {
          if (!onValueChange) {
            return;
          }

          for (const option of options) {
            if (option.value === value) {
              onValueChange({
                value,
                label: option.label,
                name,
              });
            }
          }
        },
      }),
      ...props,
    };

    return (
      <div className="grid-row-2 grid gap-y-1">
        <Label
          className={cn({
            'opacity-50 hover:cursor-not-allowed': props.disabled,
          })}
          htmlFor={name}
        >
          {label}
        </Label>
        <div className="grid-row-2 grid gap-y-2">
          <SelectRoot {...rootProps}>
            <SelectTrigger
              id={name}
              className={cn(className, {
                'border-warning shadow-none hover:border-warning hover:shadow-none focus:border-warning focus:shadow-none data-[placeholder]:text-warning':
                  invalid,
              })}
              onBlur={onBlur}
              {...(helperText && {
                'aria-describedby': `helper-text-for-${name}`,
              })}
            >
              <SelectValue placeholder={placeholder ?? 'Select an option'} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
          {helperText && (
            <HelperText id={name} invalid={invalid}>
              {helperText}
            </HelperText>
          )}
        </div>
      </div>
    );
  },
);

export default Select;
